import React, { useState } from "react";
import {
  FiChevronDown,
  FiSearch,
  FiClock,
  FiMapPin,
  FiPhone,
  FiActivity,
  FiZap,
  FiWifi,
  FiCompass,
  FiArrowRight,
  FiCheck,
  FiCopy,
  FiCrosshair,
} from "react-icons/fi";
import moment from "moment";
import UserAvatar from "../../components/UserAvatar";
import { images } from "../../utils/images";
import { AppMainModules, FMCRPermission, FMCRSubModules } from "../../utils/app-constant";
import { hasFullPermission } from "../../utils/helper";

const formatLastUpdated = (timestamp) => {
  if (!timestamp) {
    return { formatted: "N/A", relative: "No signal" };
  }
  try {
    const raw = String(timestamp).trim();
    // Parse as UTC and convert to Asia/Karachi (+05:00)
    let m = moment.utc(raw.replace(" ", "T")).utcOffset("+05:00");
    if (!m.isValid()) {
      m = moment.utc(raw).utcOffset("+05:00");
    }
    if (!m.isValid()) {
      m = moment(raw).utcOffset("+05:00");
    }

    if (!m.isValid()) {
      return { formatted: String(timestamp), relative: "Just now" };
    }

    return {
      formatted: m.format("YYYY-MM-DD hh:mm:ss A"),
      relative: m.fromNow(),
    };
  } catch (e) {
    return { formatted: String(timestamp || "N/A"), relative: "Just now" };
  }
};

const normalizeFleetStatus = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

const formatFleetStatusLabel = (value) => {
  if (!value) return "Inactive";
  const str = String(value).trim().toUpperCase();
  if (str === "IDLE") return "Idle";
  if (str === "INACTIVE") return "Inactive";
  if (str === "RUNNING") return "Running";
  if (str === "STOP") return "Stop";
  if (str === "MOVING" || str === "ACTIVE" || str === "AVAILABLE") return "Running";
  if (str === "STOPPED" || str === "ALERT") return "Stop";
  if (str === "RESTING" || str === "LOADING" || str === "UNLOADING" || str === "FUELING") return "Idle";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const FleetSidebar = ({
  loading,
  loadingMore,
  searchText,
  setSearchText,
  activeTab,
  setActiveTab,
  statusTabs = [],
  selectedIds = [],
  filteredVehicles = [],
  hasMoreVehicles,
  onLoadMoreVehicles,
  handleSelectVehicle,
  handleTrackSelected,
  trackButtonStatus = "idle",
  expandedId,
  handleToggleExpand,
  navigate,
  onOpenDrawer,
  onCenterVehicle,
  isTracked,
  deviceTypeTab = "tracker",
  setDeviceTypeTab,
}) => {
  const [copiedId, setCopiedId] = useState(null);

  const canSeeFullDetails = hasFullPermission({
    permissionSlug: FMCRPermission.SeeFullDetails,
    menuSlug: FMCRSubModules.FMCR,
    moduleSlug: AppMainModules.fmcr,
  });

  const canTrackSelected = hasFullPermission({
    permissionSlug: FMCRPermission.TrackSelectedVehicles,
    menuSlug: FMCRSubModules.FMCR,
    moduleSlug: AppMainModules.fmcr,
  });

  const isTrackButtonLoading = trackButtonStatus === "loading";
  const trackButtonLabel =
    {
      loading: "Tracking...",
      success: "Tracking Started",
      error: "Tracking Failed",
    }[trackButtonStatus] || "Track Selected Vehicles";

  const handleListScroll = (event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 120) {
      if (hasMoreVehicles && !loading && !loadingMore) {
        onLoadMoreVehicles?.();
      }
    }
  };

  const handleCopyCoords = (v, e) => {
    e.stopPropagation();
    if (v.lat !== undefined && v.lng !== undefined) {
      const coordsText = `${v.lat}, ${v.lng}`;
      navigator.clipboard.writeText(coordsText);
      setCopiedId(v.id);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  return (
    <div className="fleet_monitoring_sidebar">
      {/* Header Section */}
      <div className="fleet_sidebar_header_section">
        <div className="fleet_sidebar_title_bar">
          <div>
            <h5>Real-Time Fleet Tracking</h5>
            <span className="fleet_sidebar_subtitle">Live telematics &amp; monitoring</span>
          </div>
          <div className="fleet_device_tab_toggle">
            <button
              type="button"
              className={`fleet_device_tab_btn ${deviceTypeTab === "tracker" ? "active" : ""}`}
              onClick={() => setDeviceTypeTab("tracker")}
            >
              Tracker
            </button>
            <button
              type="button"
              className={`fleet_device_tab_btn ${deviceTypeTab === "mobile" ? "active" : ""}`}
              onClick={() => setDeviceTypeTab("mobile")}
            >
              Mobile
            </button>
          </div>
        </div>

        {/* Legend dots */}
        <div className="fleet_legend_grid">
          <div className="fleet_legend_item">
            <span className="fleet_legend_dot fleet_dot_idle"></span> Idle
          </div>
          <div className="fleet_legend_item">
            <span className="fleet_legend_dot fleet_dot_inactive"></span> Inactive
          </div>
          <div className="fleet_legend_item">
            <span className="fleet_legend_dot fleet_dot_running"></span> Running
          </div>
          <div className="fleet_legend_item">
            <span className="fleet_legend_dot fleet_dot_stop"></span> Stop
          </div>
        </div>
      </div>

      {/* Search Bar & Status Filter Tabs */}
      <div className="fleet_sidebar_controls">
        <div className="fleet_search_bar">
          <FiSearch color="#9C9FC9" size="18px" />
          <input
            type="text"
            placeholder="Search vehicle, driver, or IMEI..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          {searchText && (
            <button
              type="button"
              className="fleet_search_clear"
              onClick={() => setSearchText("")}
            >
              ×
            </button>
          )}
        </div>

        <div className="fleet_tabs_container">
          {statusTabs.map((tab) => (
            <button
              key={tab.value}
              className={`fleet_tab_item ${activeTab === tab.value ? "active" : ""}`}
              onClick={() => setActiveTab(tab.value)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Vehicle Cards List */}
      <div className="fleet_vehicle_list_container" onScroll={handleListScroll}>
        {loading ? (
          <div className="fleet_empty_list_placeholder">
            <div className="fleet_loading_spinner"></div>
            <span>Loading vehicles...</span>
          </div>
        ) : filteredVehicles.length === 0 ? (
          <div className="fleet_empty_list_placeholder">
            <span>No vehicles found</span>
          </div>
        ) : (
          filteredVehicles.map((v) => {
            const isChecked = selectedIds.includes(v.id);
            const isExpanded = expandedId === v.id;
            const lastUpdatedInfo = formatLastUpdated(v.lastUpdated);

            return (
              <div
                key={v.id}
                id={`fleet-card-${v.id}`}
                className={`fleet_vehicle_card_wrapper ${
                  isExpanded ? "expanded" : ""
                } ${isChecked ? "selected_card" : ""}`}
              >
                {/* Collapsed Card Header */}
                <div
                  className={`fleet_vehicle_list_item ${isChecked ? "selected" : ""}`}
                  onClick={() => handleToggleExpand(v.id)}
                  title="Click to expand vehicle details"
                >
                  <input
                    type="checkbox"
                    id={`fleet-check-${v.id}`}
                    className="fleet_checkbox_input"
                    checked={isChecked}
                    onChange={() => handleSelectVehicle(v.id)}
                    onClick={(e) => e.stopPropagation()}
                  />

                  <div className="fleet_vehicle_avatar">
                    <img
                      src={images.vehcileImg}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = images.vehcileImg || "";
                      }}
                      alt={v.regNumber || "Vehicle"}
                    />
                  </div>

                  <div className="fleet_vehicle_details">
                    <h6 className="fleet_vehicle_name">
                      {v.regNumber || `Vehicle #${v.id}`}
                    </h6>
                    <p className="fleet_vehicle_trip">
                      <span>Trip:</span>
                      {v.tripId && v.tripId !== "-" ? (
                        <span
                          className="fleet_vehicle_trip_link"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/trips`);
                          }}
                          title="View Trip Details"
                        >
                          {v.tripId}
                        </span>
                      ) : (
                        <span className="fleet_vehicle_no_trip">No Active Trip</span>
                      )}
                    </p>
                  </div>

                  {(() => {
                    const rawStatus =
                      deviceTypeTab === "tracker"
                        ? (v.tracker_status || v.status || "INACTIVE")
                        : (v.vehicle_status || v.status || "INACTIVE");
                    const displayLabel = formatFleetStatusLabel(rawStatus);
                    const statusKey = displayLabel.toLowerCase();
                    return (
                      <div className="fleet_vehicle_status_col">
                        <span
                          className={`fleet_status_badge fleet_badge_${statusKey}`}
                        >
                          <span className="fleet_badge_status_dot"></span>
                          {displayLabel}
                        </span>

                        <FiChevronDown
                          className={`fleet_item_arrow ${isExpanded ? "expanded" : ""}`}
                        />
                      </div>
                    );
                  })()}
                </div>

                {/* Expanded Card Body */}
                {isExpanded && (
                  <div
                    className="fleet_vehicle_expanded_details"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {/* Live Socket Sync Banner */}
                    <div className="fleet_card_sync_banner">
                      <div className="fleet_sync_indicator">
                        <span className="fleet_live_pulse_radar"></span>
                        <span className="fleet_sync_text">Live Socket Active</span>
                      </div>
                      {v.imeino && (
                        <span className="fleet_imei_badge">
                          IMEI: <b>{v.imeino}</b>
                        </span>
                      )}
                    </div>

                    {/* Prominent Last Updated Section */}
                    <div className="fleet_card_last_updated_box">
                      <div className="fleet_card_last_updated_header">
                        <div className="fleet_card_last_updated_title">
                          <FiClock className="fleet_clock_icon" />
                          <span>Last Updated</span>
                        </div>
                        <span className="fleet_live_badge">
                          <span className="fleet_live_pulse_dot"></span>
                          {lastUpdatedInfo.relative}
                        </span>
                      </div>
                      <div className="fleet_card_last_updated_timestamp">
                        {lastUpdatedInfo.formatted}
                      </div>
                    </div>

                    {/* Driver Information Row */}
                    <div className="fleet_driver_card">
                      <div className="fleet_driver_left">
                        <UserAvatar
                          username={v.driver}
                          image={v.driverImage}
                          size={34}
                          fontSize="11px"
                        />
                        <div className="fleet_driver_info">
                          <span className="fleet_driver_label">Assigned Driver</span>
                          <span
                            className="fleet_driver_name_link"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate("/driver-safety");
                            }}
                            title="View Driver Profile"
                          >
                            {v.driver || "Unassigned"}
                          </span>
                        </div>
                      </div>
                      {v.phone && v.phone !== "-" && (
                        <a
                          href={`tel:${v.phone}`}
                          className="fleet_driver_contact_btn"
                          onClick={(e) => e.stopPropagation()}
                          title={`Call ${v.phone}`}
                        >
                          <FiPhone />
                          <span>Call</span>
                        </a>
                      )}
                    </div>

                    {/* Telemetry Metric Tiles Grid */}
                    <div className="fleet_telemetry_grid">
                      {/* Speed */}
                      <div className="fleet_telemetry_tile">
                        <div className="fleet_telemetry_icon speed">
                          <FiActivity />
                        </div>
                        <div className="fleet_telemetry_meta">
                          <span className="fleet_telemetry_label">Speed</span>
                          <span className="fleet_telemetry_value">
                            {v.speed || "0 km/h"}
                          </span>
                        </div>
                      </div>

                      {/* ODO Meter */}
                      <div className="fleet_telemetry_tile">
                        <div className="fleet_telemetry_icon odo">
                          <FiCompass />
                        </div>
                        <div className="fleet_telemetry_meta">
                          <span className="fleet_telemetry_label">ODO Meter</span>
                          <span className="fleet_telemetry_value">
                            {v.odo || "-"}
                          </span>
                        </div>
                      </div>

                      {/* Fuel Volume */}
                      <div className="fleet_telemetry_tile">
                        <div className="fleet_telemetry_icon fuel">
                          <FiZap />
                        </div>
                        <div className="fleet_telemetry_meta">
                          <span className="fleet_telemetry_label">Fuel</span>
                          <span className="fleet_telemetry_value">
                            {v.fuel || "-"}
                          </span>
                        </div>
                      </div>

                      {/* Tracker Status */}
                      <div className="fleet_telemetry_tile">
                        <div className="fleet_telemetry_icon tracker">
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#16A34A"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M2 9a15 15 0 0 1 20 0" />
                            <path d="M5 13a10 10 0 0 1 14 0" />
                            <path d="M8.5 16.5a5 5 0 0 1 7 0" />
                            <circle cx="12" cy="20" r="1.5" fill="#16A34A" />
                          </svg>
                        </div>
                        <div className="fleet_telemetry_meta">
                          <span className="fleet_telemetry_label">Tracker</span>
                          <span className="fleet_telemetry_value">
                            {v.tracker_status || "Active"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Location & GPS Section */}
                    <div className="fleet_location_box">
                      <div className="fleet_location_header">
                        <div className="d-flex align-items-center gap-2">
                          <FiMapPin className="fleet_map_pin_icon" />
                          <span className="fleet_location_title">Current Location</span>
                        </div>
                        {onCenterVehicle && (
                          <button
                            type="button"
                            className="fleet_locate_btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              onCenterVehicle(v);
                            }}
                            title="Locate on Map"
                          >
                            <FiCrosshair />
                            <span>Locate</span>
                          </button>
                        )}
                      </div>
                      <p className="fleet_location_address">
                        {v.location && v.location !== "-"
                          ? v.location
                          : "Location not available"}
                      </p>

                      {typeof v.lat === "number" &&
                        !isNaN(v.lat) &&
                        typeof v.lng === "number" &&
                        !isNaN(v.lng) && (
                          <div className="fleet_coords_bar">
                            <div className="fleet_coords_pill">
                              <span className="fleet_coords_text">
                                {Math.abs(v.lat).toFixed(4)}° {v.lat >= 0 ? "N" : "S"},{" "}
                                {Math.abs(v.lng).toFixed(4)}° {v.lng >= 0 ? "E" : "W"}
                              </span>
                              <button
                                type="button"
                                className="fleet_coords_copy_btn"
                                onClick={(e) => handleCopyCoords(v, e)}
                                title="Copy Coordinates"
                              >
                                {copiedId === v.id ? (
                                  <FiCheck style={{ color: "#16A34A" }} />
                                ) : (
                                  <FiCopy />
                                )}
                              </button>
                            </div>
                          </div>
                        )}
                    </div>

                    {/* See Full Details Button */}
                    {canSeeFullDetails && (
                      <div className="fleet_card_action_bar">
                        <button
                          type="button"
                          className="fleet_see_details_btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onOpenDrawer) {
                              onOpenDrawer(v);
                            }
                          }}
                        >
                          <span>See Full Details</span>
                          <FiArrowRight className="fleet_btn_arrow_icon" />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}

        {!loading && loadingMore && (
          <div className="fleet_empty_list_placeholder">
            <div className="fleet_loading_spinner"></div>
            <span>Loading more vehicles...</span>
          </div>
        )}
      </div>

      {/* Bottom Action Area */}
      {canTrackSelected && (
        <div className="fleet_sidebar_bottom_button_area">
          <button
            className={`fleet_track_btn ${
              trackButtonStatus !== "idle" ? `fleet_track_btn_${trackButtonStatus}` : ""
            }`}
            disabled={selectedIds.length === 0 || isTrackButtonLoading}
            onClick={handleTrackSelected}
          >
            {isTrackButtonLoading && (
              <span className="fleet_track_btn_spinner"></span>
            )}
            {trackButtonLabel}
          </button>
        </div>
      )}
    </div>
  );
};

export default FleetSidebar;
